import React, { useEffect, useRef, useState } from "react";
import { Volume2, Pause, Play, Square } from "lucide-react";
import { isTTSSupported, speak, stopSpeaking, pauseSpeaking, resumeSpeaking, isSpeaking, isPaused } from "../lib/speech.js";

/**
 * ReadAloudButton — renders a single Play button when idle.
 * Once speaking, expands to Pause/Resume + Stop controls.
 * Automatically stops speech when this component unmounts (e.g. navigation).
 *
 * Props:
 *  - text:     content to read
 *  - label:    button label when idle (default "Read aloud")
 *  - testid:   data-testid prefix
 *  - compact:  smaller icon-only variant for inline use (e.g. chat bubbles)
 */
export default function ReadAloudButton({ text, label = "Read aloud", testid = "tts-btn", compact = false }) {
  const [state, setState] = useState("idle"); // idle | speaking | paused
  const ownsActive = useRef(false);
  const watchdogRef = useRef(null);

  // Poll the global speechSynthesis state while this button is active.
  // This recovers from mobile cases where onend never fires.
  useEffect(() => {
    if (state === "idle") {
      if (watchdogRef.current) {
        clearInterval(watchdogRef.current);
        watchdogRef.current = null;
      }
      return;
    }
    watchdogRef.current = setInterval(() => {
      if (!ownsActive.current) return;
      if (!isSpeaking() && !isPaused()) {
        // Engine reports nothing speaking — speech is done or failed.
        ownsActive.current = false;
        setState("idle");
      } else if (isPaused() && state === "speaking") {
        setState("paused");
      } else if (!isPaused() && isSpeaking() && state === "paused") {
        setState("speaking");
      }
    }, 700);
    return () => {
      if (watchdogRef.current) {
        clearInterval(watchdogRef.current);
        watchdogRef.current = null;
      }
    };
  }, [state]);

  useEffect(() => {
    return () => {
      // Stop speech only if this button was the one currently active
      if (ownsActive.current) {
        stopSpeaking();
        ownsActive.current = false;
      }
    };
  }, []);

  if (!isTTSSupported() || !text) return null;

  const start = () => {
    const u = speak(text, {
      onStart: () => {
        ownsActive.current = true;
        setState("speaking");
      },
      onEnd: () => {
        ownsActive.current = false;
        setState("idle");
      },
      onPause: () => setState("paused"),
      onResume: () => setState("speaking"),
    });
    if (u) {
      ownsActive.current = true;
      setState("speaking");
    }
  };
  const pause = () => {
    pauseSpeaking();
    setState("paused");
  };
  const resume = () => {
    resumeSpeaking();
    setState("speaking");
  };
  const stop = () => {
    stopSpeaking();
    ownsActive.current = false;
    setState("idle");
  };

  // --- IDLE: single Play button ---
  if (state === "idle") {
    if (compact) {
      return (
        <button
          onClick={start}
          className="p-1.5 rounded-lg hover:bg-brimi-creamSoft text-brimi-navyText/60 transition"
          title={label}
          aria-label={label}
          data-testid={testid}
        >
          <Volume2 size={14} />
        </button>
      );
    }
    return (
      <button onClick={start} className="brimi-btn-ghost text-sm" data-testid={testid}>
        <Volume2 size={14} /> {label}
      </button>
    );
  }

  // --- SPEAKING / PAUSED: control bar ---
  const wrapperBase = compact
    ? "inline-flex items-center gap-0.5 rounded-lg bg-brimi-creamSoft p-0.5"
    : "inline-flex items-center gap-1 rounded-xl bg-brimi-creamSoft p-1";
  const iconBtn = (active) =>
    `${compact ? "p-1.5" : "p-2"} rounded-lg transition ${
      active ? "bg-white text-brimi-navyText shadow-sm" : "text-brimi-navyText/60 hover:bg-white/60"
    }`;
  const iconSz = compact ? 14 : 16;

  return (
    <div className={wrapperBase} data-testid={`${testid}-controls`} role="group" aria-label="Read aloud controls">
      {state === "speaking" ? (
        <button
          onClick={pause}
          className={iconBtn(true)}
          title="Pause"
          aria-label="Pause reading"
          data-testid={`${testid}-pause`}
        >
          <Pause size={iconSz} />
        </button>
      ) : (
        <button
          onClick={resume}
          className={iconBtn(true)}
          title="Resume"
          aria-label="Resume reading"
          data-testid={`${testid}-resume`}
        >
          <Play size={iconSz} />
        </button>
      )}
      <button
        onClick={stop}
        className={iconBtn(false)}
        title="Stop"
        aria-label="Stop reading"
        data-testid={`${testid}-stop`}
      >
        <Square size={iconSz} />
      </button>
      {!compact && (
        <span className="text-xs font-medium text-brimi-navyText/70 px-2" data-testid={`${testid}-status`}>
          {state === "paused" ? "Paused" : "Reading…"}
        </span>
      )}
    </div>
  );
}
